import {GeospatialService} from '../../helper/geospatial-service';
import {AssetRendererService} from '../../renderers/asset-renderer.service';
import {AssetFieldsService} from '../../fields/asset-fields.service';
import {Injectable} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Graphic from '@arcgis/core/Graphic';
import Polyline from '@arcgis/core/geometry/Polyline';
import {GenericPopupService} from '@core/utils/engin/geospatial-viewer/popups/generic-popup.service';
import {SpatialEntityDataExtended} from '@core/utils/engin/geospatial-viewer/helper/engin-data-prepare.service';
import {LayerSettings} from '@core/utils/engin/geospatial-viewer/esri-layers/generate-layer.service';
import {VisualizationType} from '../../../../../../pages/geospatial-viewer/model/visualization';

@Injectable()
export class PolylineLayerService {
    constructor(
        private geospatialService: GeospatialService,
        private genericPopupService: GenericPopupService,
        private rendererService: AssetRendererService,
        private fieldsService: AssetFieldsService,
    ) {}

    _prepareLineSettings: LayerSettings = {
        showTooltip: true,
        suppressTooltipTitle: false,
        metrics: [],
        showStaticLabel: false,
        staticLabelProps: {},
    };

    /**
     * Prepare polyline layer (geometry type = polyline).
     * Works for point assets (lines) or regions (multi-lines).
     * @return FeatureLayer
     */
    public preparePolylineLayer(
        linearData: SpatialEntityDataExtended[],
        renderedVisualizationType: VisualizationType,
        id: string,
        theme: string,
        settings?: LayerSettings,
    ): FeatureLayer {
        // Get settings: priority of provided settings, else default settings i.e. allows settings to be partial set
        const finalSettings: LayerSettings = {
            ...this._prepareLineSettings,
            ...settings,
        };

        // Load Esri dependencies from AMD
        const source = this.createPolylineGraphics(linearData, settings?.metrics[0] || '');

        let linePopupTemplate = null;
        if (finalSettings?.showTooltip) {
            linePopupTemplate = {
                title: finalSettings?.suppressTooltipTitle ? '' : '{id}',
                outFields: ['*'],
                content: this.genericPopupService.getGenericMetricTooltipFunction.bind(
                    this.genericPopupService,
                    finalSettings?.metrics || [],
                    renderedVisualizationType,
                ),
            };
        }

        return new FeatureLayer({
            id: id ? id : 'asset_line_layer',
            objectIdField: 'ObjectID',
            source: source,
            renderer: this.rendererService.assetLineRenderer(theme),
            fields: this.fieldsService.getMapFields(),
            outFields: ['*'],
            popupTemplate: linePopupTemplate,
            // @ts-ignore
            labelingInfo: finalSettings.staticLabelProps
                ? [
                      {
                          labelExpressionInfo: {
                              expression: finalSettings.staticLabelProps.expression,
                          },
                          symbol: finalSettings.staticLabelProps.symbol,
                          labelPlacement: finalSettings.staticLabelProps.labelPlacement,
                      },
                  ]
                : {},
        });
    }

    /*
     * Create array of Graphics, type = polyline. Return Graphic[]
     */
    private createPolylineGraphics(lineData: SpatialEntityDataExtended[], metric?: string): Graphic[] {
        return lineData.map((area) => {
            const geometry = new Polyline({
                paths: area.geoJsonGeometry.coordinates,
            });

            return new Graphic({
                geometry: geometry,
                attributes: {
                    ...area,
                    ...(!!metric ? {metric: metric} : {}),
                },
            });
        });
    }
}
